import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common';
import { ContentPopup } from 'components/ContentPopup.jsx';
import QuoteBuilderForm from 'components/quote_builder/QuoteBuilderForm.jsx';
import SettingsTabs from 'components/quote_builder/SettingsTabs.jsx';
import { ListenCondition } from 'components/quote_builder/form_fields/index.js';

const UserUnitPrice = ({ config, setValue, value, values, windowValue, disabled, setFetchInProgress, options, item, navSection, requestPublishItemOnChange, requestGetExtraItems, navSectionColumnType }) => {
  // A listen-condition captures fetchUserUnitPrices once at mount (its useEffect has [] deps), and the filter
  // clauses it re-runs read form values through v(). Reading `values` directly would bind to the MOUNT-time
  // object, so a refetch triggered later (e.g. on carton_gsm_changed) would re-filter against a stale GSM/colour.
  // Read through a ref refreshed every render, so v() always sees the current values even from that closure.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const v = (key) => { return valuesRef.current[key] }
  const USER_UNIT_PRICE_CHANGED_WITH_ITEM_EVENT_KEY = 'quote_builder.user_unit_price_changed_with_item';
  const USER_UNIT_PRICE_ID_CHANGED_EVENT_KEY = '_user_unit_price_id_changed';

  const [allUserUnitPrices, setAllUserUnitPrices] = useState([]);
  const [userUnitPrices, setUserUnitPrices] = useState([]);
  const [configForPopUpForm, setConfigForPopUpForm] = useState({});
  const [selectedUnitPriceSummary, setSelectedUnitPriceSummary] = useState('');
  const newValueAddedID = useRef();
  const defaultSelectedOnLoad = useRef(false);
  const [editingMode, setEditingMode] = useState(false);
  const [listingMode, setListingMode] = useState(false);
  const [copyFromSystemMode, setCopyFromSystemMode] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);
  const VALUE_NOT_AVAILABLE = 'not-available'
  const VALUE_ALL = 'all';
  const VALUE_EMPTY = "";
  const VALUE_NEW = "new-attribute";
  const newUserPricePopupRef = useRef();
  const editUserPricePopupRef = useRef();
  const listUserPricePopupRef = useRef();
  const [notAvailableReason, setNotAvailableReason] = useState('');

  const [readOnly, setReadOnly] = useState(false);
  const [readOnlyMessage, setReadOnlyMessage] = useState('');

  // A filter clause can reference a section option that has not loaded yet (options arrive from an async
  // /quote-builder/sections fetch, so options.printing_machine_accepted_printing_types is undefined until
  // it does), and eval'ing the clause then throws. Fail CLOSED on that -- treat the row as not passing --
  // exactly as the filtered-out-reason display already does (firstFilteredOutReason). Without this the throw
  // aborts the whole filter, so the fetch callback never sets the filtered list and the dropdown freezes on
  // a stale, unfiltered one.
  const clausePasses = (user_unit_price, clause) => {
    try { return eval(clause); } catch(evaluationError) { return false; }
  }

  const filteredData = (data, filter_eval) => {
    if(config.filter != undefined && filter_eval == undefined) {
      data = data.filter((user_unit_price) => clausePasses(user_unit_price, config.filter))
    }

    if(config.filters != undefined && filter_eval == undefined) {
      for(let i=0; i< config.filters.length; i++) {
        data = data.filter((user_unit_price) => clausePasses(user_unit_price, config.filters[i].filter))
      }
    }

    if(filter_eval != undefined) {
      data = data.filter((user_unit_price) => clausePasses(user_unit_price, filter_eval))
    }

    return data;
  }

  const fetchUserUnitPrices = (filter_eval) => {
    if(disabled) { return };
    setFetchInProgress(true);
    GetRequest({
      url: `/quote-builder/user_unit_prices/list/section/${config.key}/${window.locale}`,
      callback: (data) => {
        setAllUserUnitPrices(data);

        setFetchInProgress(false);
        data = filteredData(data, filter_eval);

        setUserUnitPrices(data);
      }
    })
  };

  useEffect(() => {
    if(disabled) { return };

    fetchUserUnitPrices();
  },[]);


  const valueTobeSetAfterFetch = useRef(null);

  useEffect(() => {
    if(!disabled && config.view_selected_summary) {
      updateSelectedUnitPriceSummary();
    }

    if(userUnitPrices.length == 0 && value != undefined && value != 'not-available') {
      valueTobeSetAfterFetch.current = value;
    }

  }, [value, userUnitPrices])

  useEffect(() => {
    if(valueTobeSetAfterFetch.current != null && userUnitPrices.length > 0) {
      const stashedValue = valueTobeSetAfterFetch.current;
      // Consume the stash once. It exists to carry the saved value across the single gap before the list
      // loads; re-applying it on EVERY later refetch is what let a filtered-out id come back after it had
      // been reset to not-available (an oscillation). And only re-apply a value that is actually selectable
      // now -- a value the filters exclude must not be resurrected, so selectionIsFilteredOut() can reset it.
      valueTobeSetAfterFetch.current = null;
      if(userUnitPrices.some((userUnitPrice) => userUnitPrice.id == stashedValue)) {
        setValue(config.name, (_p) => { return stashedValue })
        setValue('updated_at', () => { return Date.now() } );
      }
    }
  }, [userUnitPrices] )

  useEffect(() => {
    if(config.refetch_list_on_window_value_change) {
      fetchUserUnitPrices();
    }

  }, [windowValue]);

  const setValueToFirstAvailableOption = () => {
    setValue(config.name, userUnitPrices.length > 0 ? (p) => { return userUnitPrices[0].id } : (p) => { return VALUE_NOT_AVAILABLE });
    setValue('updated_at', () => { return Date.now() } );
  }

  const setValueToNotAvailable = () => {
    setValue(config.name,  (p) => { return VALUE_NOT_AVAILABLE });
    setValue('updated_at', () => { return Date.now() } );
  }

  // A saved selection can be a machine the current filters exclude (e.g. its thickness or sheet size no
  // longer fits the section). The <select> then shows "Not included" -- the stored id matches no selectable
  // option, so the browser falls back to the first one -- while the id is still stored and still priced: an
  // inconsistent state where the dropdown reads not-included but a result shows. Reconcile it by resetting a
  // stranded selection to not-available, so the result clears to match the dropdown. Gated to pickers that
  // filter AND offer not-available, and to a LIVE list (at least one option passed, so the filters are not
  // merely failing closed on options that have not loaded yet -- that guards a valid pick from being wiped
  // during the async-options race). filteredOutUnitPrices() is the fetched-but-filtered-out set, so a value
  // found there is a real machine the filters dropped, never an empty / not-available / new-attribute value.
  const selectionIsFilteredOut = () => {
    return !config.select_first_item_on_load &&
      config.not_available_enabled &&
      (config.filter != undefined || config.filters != undefined) &&
      userUnitPrices.length > 0 &&
      filteredOutUnitPrices().some((userUnitPrice) => userUnitPrice.id == value);
  }

  useEffect(() => {
    if(disabled) { return };

    if(newValueAddedID.current != undefined) {
      setValue(config.name,  (p) => { return newValueAddedID.current } );
      setValue('updated_at', ( ) => { return Date.now() } );
      return;
    }

    if(selectionIsFilteredOut()) {
      setValueToNotAvailable();
      checkForValueInUserPricesRequired.current = false;
      return;
    }

    // Only a picker whose filters read section options (config.filter/filters) can fail closed to an empty list
    // while those options are still loading, so an empty list there is ambiguous -- resetting on it would wipe a
    // still-valid pick during the async-options race (the same guard selectionIsFilteredOut carries), and the flag
    // stays set so re-validation defers until a non-empty list arrives. A picker with no such filters -- e.g.
    // coating, filtered only by the listener's filter_eval over its OWN rows -- never fails closed, so an empty
    // list there is a genuine no-match that MUST reset. So the non-empty-list guard applies to the former only.
    const emptyListMayBeFailClosed = config.filter != undefined || config.filters != undefined;
    if(checkForValueInUserPricesRequired.current && (userUnitPrices.length > 0 || !emptyListMayBeFailClosed) && value != VALUE_NOT_AVAILABLE && value != VALUE_ALL && value != undefined && value != null) {
      let valueFound = false;
      for(let i=0; i< userUnitPrices.length; i++) {
        if(userUnitPrices[i].id == value) {
          valueFound = true;
        }
      }

      if(!valueFound && config.not_available_enabled) {
        setValueToNotAvailable();
        checkForValueInUserPricesRequired.current = false;
        return;
      }

      if(!valueFound && !config.not_available_enabled) {
        setValueToFirstAvailableOption();
        checkForValueInUserPricesRequired.current = false;
        return;
      }
    }

    if(config.select_first_item_on_load) {
      setValueToFirstAvailableOption();
      return;
    }

    // Issue #5186: when no price is saved yet, pre-select the one matching the default thickness
    // for the part's material/machine (config.select_default_on_load_eval, evaluated against each
    // user_unit_price + options). Done once; a saved selection (including 'not-available') is kept.
    if(config.select_default_on_load_eval && !defaultSelectedOnLoad.current && !window.user_quote_finalised && (value == undefined || value == VALUE_EMPTY) && userUnitPrices.length > 0) {
      for(let i=0; i< userUnitPrices.length; i++) {
        let user_unit_price = userUnitPrices[i];
        if(eval(config.select_default_on_load_eval)) {
          setValue(config.name, (p) => { return userUnitPrices[i].id });
          setValue('updated_at', () => { return Date.now() });
          defaultSelectedOnLoad.current = true;
          return;
        }
      }
    }

  },[userUnitPrices]);

  const onNewUserPricePopupClose = () => {
    callRequestPublishItemOnChange();
    fetchUserUnitPrices();
    fetchSystemUnitPrices();
    publishUserUnitPriceListReFetchRequired();
  }

  const publishUserUnitPriceListReFetchRequired = () => {
    document.dispatchEvent(new CustomEvent(USER_UNIT_PRICE_ID_CHANGED_EVENT_KEY, { detail: { id: value, key: config.key, navSectionColumnType: navSectionColumnType } } ));
  }

  useEffect(() => {
    let listener = (e) => {
      if(e.detail.key == config.key && e.detail.navSectionColumnType != navSectionColumnType && e.detail.id != 'not-available') {
        fetchUserUnitPrices();
      }
    }

    document.addEventListener(USER_UNIT_PRICE_ID_CHANGED_EVENT_KEY,  listener);
    return () => { document.removeEventListener(USER_UNIT_PRICE_ID_CHANGED_EVENT_KEY, listener);}
  }, []);

  const newUserPricePopup = () => {
    if(Object.keys(configForPopUpForm).length == 0) return;
    let currentValue = value;
    let callback = (id) => {
      newValueAddedID.current = id;

      if(newValueAddedID.current != undefined) {
        onNewUserPricePopupClose();
      } else {
        setValueToNotAvailable();
      }

      if (newUserPricePopupRef.current) {
        newUserPricePopupRef.current.style.display = "none";
      }
      checkForValueInUserPricesRequired.current = true;
      setEditingMode(false);
      setListingMode(false);
      setCopyFromSystemMode(false);
    }
    let valuesContext = {};

    if(config.create_context != undefined) {
      for (const [contextKey, contextValue] of Object.entries(config.create_context)) {
        valuesContext[contextKey] = eval(contextValue)
      }
    }
    // what is this context for? we can pass values that can be used by config.create_read_only_values or anything like that
    // QuoteBuilderForm.jsx looks like it does not use context if you read the codebase but its called from sections.yml
    let content = <QuoteBuilderForm config={configForPopUpForm} showingState="new" afterSave={callback} afterCancel={callback} readOnlyValues={config.create_read_only_values} context={valuesContext} options={options} navSection={navSection} />;
    let response = <ContentPopup myRef={newUserPricePopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={callback} />;

    return response;
  }

  const checkForValueInUserPricesRequired = useRef(false);

  const editPricePopup = () => {
    if(Object.keys(configForPopUpForm).length == 0) return;

    let callback = () => {
      setEditingMode(false);
      setListingMode(false);
      setCopyFromSystemMode(false);
      onNewUserPricePopupClose();
      setValue('updated_at', () => { return Date.now() } );
      callRequestPublishItemOnChange();
      if (editUserPricePopupRef.current) {
        editUserPricePopupRef.current.style.display = "none";
      }
      publishUserUnitPriceListReFetchRequired();
      checkForValueInUserPricesRequired.current = true;
    }

    let content = <QuoteBuilderForm config={configForPopUpForm} showingState="edit" afterSave={callback} afterCancel={callback} editStateId={value} linkToSettings={true} />;
    let response = <ContentPopup myRef={editUserPricePopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={callback} />;

    return response;
  }

  const copyFromSystemPopup = () => {
    if(Object.keys(configForPopUpForm).length == 0) return;

    let callback = (id) => {
      setEditingMode(false);
      setListingMode(false);
      setCopyFromSystemMode(false);
      newValueAddedID.current = id;
      if(newValueAddedID.current != undefined) {
        onNewUserPricePopupClose();
      } else {
        setValueToNotAvailable();
      }

      if (newUserPricePopupRef.current) {
        newUserPricePopupRef.current.style.display = "none";
      }
      checkForValueInUserPricesRequired.current = true;
    }

    let content = <QuoteBuilderForm config={configForPopUpForm} showingState="copy_from_system" afterSave={callback} afterCancel={callback} editStateId={copyFromSystemId} linkToSettings={true} />;
    let response = <ContentPopup myRef={editUserPricePopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={callback} />;

    return response;
  }

  const listingModePopup = () => {
    if(Object.keys(configForPopUpForm).length == 0) return;

    let callback = () => {
      setEditingMode(false);
      setListingMode(false);
      setCopyFromSystemMode(false);
      onNewUserPricePopupClose();
      callRequestPublishItemOnChange();
      if (listUserPricePopupRef.current) {
        listUserPricePopupRef.current.style.display = "none";
      }
      checkForValueInUserPricesRequired.current = true;
    }

    let content = <><br /><SettingsTabs
      config={configForPopUpForm}
      showingState="list"
      afterSave={callback}
      afterCancel={callback}
      editStateId={value}
      hideDeleteFromList={false}
    /></>;

    // minWidth (not width) because .modal-content-wide sets width with !important; min-width
    // still wins in layout. The list popup holds tab rows plus a full price table, so the
    // default 50% sometimes wraps the tabs.
    let response = <ContentPopup myRef={listUserPricePopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={callback} modalStyle={{ minWidth: '80%' }} />;

    return response;
  };

  const listAllItems = (e) => {
    e.preventDefault();
    setListingMode(true);
  }

  const handleEdit = (e) => {
    setEditingMode(true);
    e.preventDefault();
  }

  useEffect(() => {
    if(disabled) { return };
    if(config.create_enabled || config.edit_selected_item_enabled) {
      setFetchInProgress(true);
      GetRequest({
        url: `/quote-builder/config/settings/user_unit_prices/${config.key}`,
        useCache: true,
        callback: (data) => {
          setFetchInProgress(false);
          setConfigForPopUpForm(data);
        }
      })
    }
  }, []);

  const updateSelectedUnitPriceSummary = () => {
    setSelectedUnitPriceSummary('');
    for(let i = 0; i < userUnitPrices.length; i++) {
      if(parseInt(value) == parseInt(userUnitPrices[i].id)) {
        setSelectedUnitPriceSummary(userUnitPrices[i].summary);
      }
    }
  }

  const setValueWrapped = (e) => {
    let price_id = e.target.value;
    let isSystem = false;

    for(let i=0; i< systemUnitPrices.length; i++) {
      if(parseInt(price_id) == parseInt(systemUnitPrices[i].id)) {
        isSystem = true;
      }
    }
    if(isSystem) {
      setCopyFromSystemId(price_id);
      setCopyFromSystemMode(true);
    }else {
      setValue(config.name, price_id);
      setValue('updated_at', Date.now());
    }
  }

  // Just the "filtered by ..." reason (without the "My unit prices" prefix), so myUnitPricesLabel
  // can compose the header parts in order.
  const filterWording = () => {
    if(config.filter_wording_eval != undefined) {
      return t( 'quote_builder.' + eval(config.filter_wording_eval)) + ' ' + t('quote_builder.unit_prices')
    }

    if(config.filter_wording != undefined) {
      return t('quote_builder.' + config.filter_wording)
    }
  }

  const filteredNumbers = () => {
    return t('quote_builder.showing_filtered_numbers').replace('%{showingNumber}', userUnitPrices.length).replace('%{all}', allUserUnitPrices.length)
  }

  // list_filtered_out_results_with_reasons (sections.yml, opt-in per picker): every own price the
  // filters did NOT keep is listed as a disabled row carrying the FIRST reason it did not make the cut
  // (each filters clause can carry a reason translation key; clauses without one fall back to the
  // picker's filter wording), so the user can always see WHY a machine is missing -- not only when the
  // filters leave zero selectable prices. Opt-in, because every enabled picker needs its reasons
  // written. Evaluated at render time so the reasons always use the current form values.
  const listFilteredOutResultsWithReasons = () => {
    return config.list_filtered_out_results_with_reasons == true &&
      (config.filter != undefined || config.filters != undefined);
  }

  // The own prices the filters dropped -- everything fetched minus what stayed selectable. userUnitPrices
  // is a subset of allUserUnitPrices (same rows), so identity by id is exact and also catches the rows the
  // filter dropped by failing CLOSED on a clause that threw.
  const filteredOutUnitPrices = () => {
    const availableIds = new Set(userUnitPrices.map((price) => price.id));
    return allUserUnitPrices.filter((price) => !availableIds.has(price.id));
  }

  const firstFilteredOutReason = (user_unit_price) => {
    const filterClauses = config.filters != undefined ? config.filters : [{ filter: config.filter }];
    for(let i = 0; i < filterClauses.length; i++) {
      let clausePasses = true;
      try { clausePasses = eval(filterClauses[i].filter); } catch(evaluationError) { clausePasses = false; }
      if(!clausePasses) {
        return filterClauses[i].reason != undefined ? t(filterClauses[i].reason) : filterWording();
      }
    }
    return filterWording();
  }

  const filteredOutOptionLabel = (user_unit_price) => {
    const filteredOutReason = firstFilteredOutReason(user_unit_price);
    if (filteredOutReason == undefined || filteredOutReason == '') { return user_unit_price.description; }
    return `${user_unit_price.description} - ${filteredOutReason}`;
  }

  const callRequestPublishItemOnChange = () => {
    if(config.publish_item_on_change && config.publish_item_if_eval == undefined) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }

    if(config.publish_item_on_change && config.publish_item_if_eval != undefined && eval(config.publish_item_if_eval) ) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }
  }

  useEffect(() => {
    callRequestPublishItemOnChange();

    if(config.get_extra_items_on_change_if == undefined && config.get_extra_items_on_change) {
      requestGetExtraItems( { key: config.key, value: value } );
    }

    if(config.get_extra_items_on_change_if != undefined && eval(config.get_extra_items_on_change_if)) {
      requestGetExtraItems( { key: config.key, value: value } );
    }

    if(config.not_available_if != undefined && eval(config.not_available_if)) {
      setNotAvailableReason(t(config.not_available_if_reason));
    } else {
      setNotAvailableReason('');
    }

  }, [value])


  if(config.listen_conditions != undefined) {
    for(let i=0; i < config.listen_conditions.length; i++) {

      useEffect(() => {
        let listener = ListenCondition.listener({
          config: config,
          condition: config.listen_conditions[i],
          item: item,
          navSection: navSection,
          setValue: setValue,
          GetRequest: GetRequest,
          setReadOnly: setReadOnly,
          setReadOnlyMessage: setReadOnlyMessage,
          setNotAvailable: setNotAvailable,
          setNotAvailableReason: setNotAvailableReason,
          options: options,
          navSectionColumnType: navSectionColumnType,
          fetchUserUnitPrices: fetchUserUnitPrices,
          fetchSystemUnitPrices: fetchSystemUnitPrices,
          checkForValueInUserPricesRequired: checkForValueInUserPricesRequired,
        });

        document.addEventListener(config.listen_conditions[i].listen, listener);
        return () => { document.removeEventListener(config.listen_conditions[i].listen, listener);}

      }, []);
    }
  }

  //create from system start
  const [systemUnitPrices, setSystemUnitPrices] = useState([]);
  const [copyFromSystemId, setCopyFromSystemId] = useState(undefined);

  const default_system_list_path = '/quote-builder/system_user_unit_prices/list/settings_list/:key/:lng';
  const [createBtnEnabled, setCreateBtnEnabled] = useState('disabled');
  const [createFromSystemEnabled, setCreateFromSystemEnabled] = useState(false);

  const fetchSystemUnitPrices = (filter_eval) => {
    GetRequest({
      url: default_system_list_path.replace(':key', config.key).replace(':lng', window.locale),
      callback: (data) => {
        data = filteredData(data, filter_eval);
        setSystemUnitPrices(data);
        setCreateFromSystemEnabled(() => { return data.length > 0});
        setCreateBtnEnabled( () => { return '' } );
      }
    })
  }

  useEffect(() => { fetchSystemUnitPrices() } , []);
 //create from system end

  if(notAvailableReason != undefined && notAvailableReason != '') {
    return <tr>
      <td colSpan="2">
        <div className="well">
          <i className="fa fa-exclamation-circle"></i>  <small> {notAvailableReason} </small>
        </div>
      </td>
    </tr>;
  }

  // "My unit prices · showing 1 of 2 · filtered by metal strip type" -- the group name, then (only
  // when the filter hides some) the shown/total count, then the reason. The reason is lowercased so
  // it reads as a continuation of the middot header.
  const myUnitPricesLabel = () => {
    let parts = [t('quote_builder.my_unit_prices')];
    // Show the shown/total count whenever some own prices are hidden -- this covers both config.filter
    // pickers and the ones filtered via a listen_conditions filter_eval (e.g. coating material).
    if(allUserUnitPrices.length != userUnitPrices.length) { parts.push(filteredNumbers()); }
    // The "filtered by ..." reason only exists for config.filter/filters pickers.
    const hasConfigFilter = config.filter != undefined || config.filters != undefined;
    const wording = hasConfigFilter ? filterWording() : undefined;
    if(wording != undefined && wording != '') { parts.push(wording.charAt(0).toLowerCase() + wording.slice(1)); }
    return parts.join(' · ');
  }
  return (
    <tr>
      <td>
        {capitalizeFirstLetter(`${config.label != undefined ? t(config.label) : ''}${config.label_translated != undefined ? config.label_translated : ''}${config.hide_price_label_suffix ? '' : ` ${t('quote_builder.price')}`}`)}
        {config.helper_text != undefined && (
          <><br /><small className="form-text text-muted" dangerouslySetInnerHTML={{ __html: t(config.helper_text) }} /></>
        )}
      </td>
      <td align="right">

        <select value={value}
          onChange={setValueWrapped}
          name={config.name}
          disabled={readOnly}
          className={`form-control ${(notAvailable) ? 'disabledForm' : ''}`}
        >
          {config.empty_option_enabled && <optgroup label=""> <option key={VALUE_EMPTY} value={VALUE_EMPTY}> </option> </optgroup> }
          {config.not_available_enabled && <optgroup label=""> <option key="n/a" value={VALUE_NOT_AVAILABLE}> {t('quote_builder.not_included')} </option> </optgroup> }
          {config.all_option_enabled && <optgroup label=""> <option key="all" value={VALUE_ALL}> {t('quote_builder.all_option')} </option> </optgroup> }

          {allUserUnitPrices.length > 0 && <optgroup label={myUnitPricesLabel()}>
            {/* When a filter hides every own price, keep the group (and its "· showing 0 of N ·
                filtered by ..." label) visible with a disabled placeholder -- native selects drop an
                empty optgroup's label, so the user would otherwise get no hint the list is filtered. */}
            {userUnitPrices.length === 0 && !listFilteredOutResultsWithReasons() && <option disabled value={VALUE_EMPTY}>{t('quote_builder.no_matching_unit_prices')}</option>}
            {listFilteredOutResultsWithReasons() && filteredOutUnitPrices().map((userUnitPrice) => {
              return <option disabled key={`filtered-out-${userUnitPrice.id}`} value={VALUE_EMPTY}>
                {filteredOutOptionLabel(userUnitPrice)}
              </option>
            })}
            {userUnitPrices.map((userUnitPrice) => {
              return <option
                key={userUnitPrice.id}
                data-is-system="false"
                value={userUnitPrice.id}> { userUnitPrice.name != '' && userUnitPrice.name !=  undefined && `[${userUnitPrice.name}]`}
                {userUnitPrice.description}{!userUnitPrice.is_system && userUnitPrice.price_with_currency_formatted != '' && userUnitPrice.price_with_currency_formatted != undefined && `, ${userUnitPrice.price_with_currency_formatted}`}
              </option>
            })}
          </optgroup>
          }

          {systemUnitPrices.length > 0  && (<optgroup label={t('quote_builder.optgroup_system_prices')}>

            {systemUnitPrices.map((userUnitPrice) => {
              return <option
                key={userUnitPrice.id}
                data-is-system="true"
                value={userUnitPrice.id}>
                {userUnitPrice.description_for_system}
              </option>
            })}
          </optgroup>)}

          {config.create_enabled && <optgroup label=""> <option key="new" value={VALUE_NEW} disabled={createBtnEnabled == 'disabled'}> {t(config.create_cta)} </option> </optgroup> }
        </select>

        {config.view_selected_summary && selectedUnitPriceSummary != '' && (
          <span>
            <small>
              <div dangerouslySetInnerHTML={{__html: selectedUnitPriceSummary}}></div>
            </small>
            {config.edit_selected_item_enabled && (<> &nbsp; <small> <a href="" onClick={handleEdit}> <i className="fa fa-pencil-square-o" aria-hidden="true"></i>  {t('quote_builder.edit')} </a></small> <br /></>)}
          </span>
        )}

        {allUserUnitPrices.length > 0 && (
         <> <small> <a onClick={listAllItems} href="#"> <i className="fa fa-list" aria-hidden="true"></i> {t('quote_builder.show_all_items')} </a> </small> <br /> </>
        )}

        {readOnlyMessage != '' && <> <small className="q-read-only-message"> {readOnlyMessage} <br /> </small> </> }
        {/* Footer note carries only a trailing <br />, like its siblings above; a leading one would
            stack on the preceding element's trailing break into a blank line above the note. */}
        {config.footer_note != undefined && (<> <small> {t(config.footer_note)} <br /> </small></>)}
        {config.create_enabled && value == VALUE_NEW && newUserPricePopup()}
        {config.edit_selected_item_enabled && editingMode && editPricePopup()}
        {config.create_enabled && copyFromSystemMode && copyFromSystemPopup()}
        {listingMode && listingModePopup()}
      </td>
    </tr>
  )
}

export default UserUnitPrice;
